import { useState, useCallback } from 'react';
import { getCSRFToken } from 'lib/request';

type Method = 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface UseEditableSheetOptions {
  url: string;
  method?: Method;
  payloadKey?: string;
  onSuccess?: (formData: Record<string, any>, responseData?: any) => void;
  onDelete?: (context: any, responseData?: any) => void;
}

interface SheetState {
  isOpen: boolean;
  formData: Record<string, any>;
  isSubmitting: boolean;
  sheetTitle: string;
  errors: Record<string, string>;
  url: string;
  method: Method;
}

export function useEditableSheet({
  url: defaultUrl,
  method: defaultMethod = 'PATCH',
  payloadKey,
  onSuccess,
  onDelete,
}: UseEditableSheetOptions) {
  const [state, setState] = useState<SheetState>({
    isOpen: false,
    formData: {},
    isSubmitting: false,
    sheetTitle: '',
    errors: {},
    url: defaultUrl,
    method: defaultMethod,
  });

  const open = useCallback((title: string, initialData: Record<string, any>, options?: { url?: string; method?: Method }) => {
    setState(prev => ({
      ...prev,
      isOpen: true,
      sheetTitle: title,
      formData: initialData,
      errors: {},
      url: options?.url || defaultUrl,
      method: options?.method || defaultMethod,
    }));
  }, [defaultUrl, defaultMethod]);

  const close = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const handleFormChange = useCallback((name: string, value: any) => {
    setState(prev => {
      const { [name]: _removed, ...restErrors } = prev.errors;
      return { ...prev, formData: { ...prev.formData, [name]: value }, errors: restErrors };
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (state.isSubmitting) return;
    setState(prev => ({ ...prev, isSubmitting: true }));

    try {
      const payload = payloadKey
        ? { [payloadKey]: state.formData }
        : state.formData;

      const response = await fetch(state.url, {
        method: state.method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken(),
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && !data.errors) {
        onSuccess?.(state.formData, data);
        close();
      } else if (data.errors) {
        setState(prev => ({ ...prev, errors: data.errors }));
      }
    } catch {
      // network error handled silently
    } finally {
      setState(prev => ({ ...prev, isSubmitting: false }));
    }
  }, [state, payloadKey, onSuccess, close]);

  const handleDelete = useCallback(async (deleteUrl: string, context?: any) => {
    if (state.isSubmitting) return;
    setState(prev => ({ ...prev, isSubmitting: true }));

    try {
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': getCSRFToken(),
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        onDelete?.(context, data);
        close();
      }
    } catch {
      // network error handled silently
    } finally {
      setState(prev => ({ ...prev, isSubmitting: false }));
    }
  }, [state.isSubmitting, onDelete, close]);

  const getError = useCallback((field: string) => {
    return state.errors[field];
  }, [state.errors]);

  return {
    isOpen: state.isOpen,
    formData: state.formData,
    isSubmitting: state.isSubmitting,
    sheetTitle: state.sheetTitle,
    errors: state.errors,
    open,
    close,
    handleFormChange,
    handleSave,
    handleDelete,
    getError,
  };
}
